import {
  integrationApi,
  type Integration,
  type Project,
} from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw, Search, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useFormError } from '@/hooks/use-form-errors';
import { formatRelative } from '@/lib/utils';
import { ConnectSearchConsoleDialog } from './connect-search-console-dialog';
import { ConfirmDialog } from './confirm-dialog';

const STRIPE_RESTRICTED_KEYS_URL =
  'https://dashboard.stripe.com/apikeys/create?name=GainSignal';

/**
 * Read permissions the key needs. Listed in the dialog so a person creating
 * the key in Stripe can tick them in one pass; the server re-checks each one
 * and names any that are missing.
 */
const REQUIRED_PERMISSIONS = [
  'Account information',
  'Customers',
  'Subscriptions',
  'Invoices',
  'Charges',
  'Checkout Sessions',
  'Refunds',
  'Disputes',
  'Products',
  'Prices',
  'Coupons',
  'Events',
];

const REVENUECAT_KEYS_URL = 'https://app.revenuecat.com/settings/api-keys';

const STATUS_TONE: Record<
  Integration['status'],
  'neutral' | 'accent' | 'success' | 'warning' | 'critical'
> = {
  connected: 'accent',
  backfilling: 'accent',
  live: 'success',
  error: 'critical',
  disconnected: 'neutral',
};

export const ConnectStripeDialog = ({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [apiKey, setApiKey] = React.useState('');

  const close = () => {
    setApiKey('');
    clearError();
    onOpenChange(false);
  };

  const mutation = useMutation({
    mutationFn: () => integrationApi.connectStripe(project.id, { apiKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations', project.id],
      });
      close();
    },
    onError: captureError,
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            clearError();
            mutation.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Connect Stripe</DialogTitle>
            <DialogDescription>
              Paste a restricted key. GainSignal never needs a full secret key
              and will refuse one.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              {error ? <Alert tone="error">{error}</Alert> : null}

              <Field
                label="Restricted API key"
                htmlFor={`stripe-key-${project.id}`}
                hint="Starts with rk_live_ or rk_test_. Stored encrypted, shown to nobody."
              >
                <Input
                  id={`stripe-key-${project.id}`}
                  name="apiKey"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  required
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  placeholder="rk_live_…"
                />
              </Field>

              <div className="rounded-lg border border-border bg-surface-muted px-3 py-2.5 text-xs text-ink-muted">
                <p>
                  <a
                    href={STRIPE_RESTRICTED_KEYS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-accent underline-offset-2 hover:underline"
                  >
                    Create a restricted key in Stripe
                  </a>{' '}
                  with <span className="font-medium text-ink">Read</span> access
                  to the resources below, plus{' '}
                  <span className="font-medium text-ink">Write</span> access to
                  Webhook Endpoints:
                </p>
                <p className="mt-1 leading-relaxed">
                  {REQUIRED_PERMISSIONS.join(' · ')}
                </p>
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              <Plug />
              Connect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ConnectRevenueCatDialog = ({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [apiKey, setApiKey] = React.useState('');
  const [revenueCatProjectId, setRevenueCatProjectId] = React.useState('');
  const [environment, setEnvironment] = React.useState<
    'production' | 'sandbox'
  >('production');
  const close = () => {
    setApiKey('');
    setRevenueCatProjectId('');
    setEnvironment('production');
    clearError();
    onOpenChange(false);
  };
  const mutation = useMutation({
    mutationFn: () =>
      integrationApi.connectRevenueCat(project.id, {
        apiKey,
        revenueCatProjectId,
        environment,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations', project.id],
      });
      close();
    },
    onError: captureError,
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            clearError();
            mutation.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Connect RevenueCat</DialogTitle>
            <DialogDescription>
              GainSignal creates an authenticated webhook and imports your
              existing customers, subscriptions, purchases, and lifecycle
              events.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              {error ? <Alert tone="error">{error}</Alert> : null}
              <Field
                label="RevenueCat project ID"
                htmlFor={`revenuecat-project-${project.id}`}
                hint="Found in Project settings. This is not an app ID."
              >
                <Input
                  id={`revenuecat-project-${project.id}`}
                  required
                  value={revenueCatProjectId}
                  onChange={(event) =>
                    setRevenueCatProjectId(event.target.value)
                  }
                  placeholder="proj…"
                  spellCheck={false}
                />
              </Field>
              <Field
                label="V2 secret API key"
                htmlFor={`revenuecat-key-${project.id}`}
                hint="Starts with sk_. Stored encrypted and never returned."
              >
                <Input
                  id={`revenuecat-key-${project.id}`}
                  type="password"
                  autoComplete="off"
                  required
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  placeholder="sk_…"
                  spellCheck={false}
                />
              </Field>
              <Field
                label="Environment"
                htmlFor={`revenuecat-environment-${project.id}`}
              >
                <Select
                  value={environment}
                  onValueChange={(value) =>
                    setEnvironment(value as 'production' | 'sandbox')
                  }
                >
                  <SelectTrigger aria-label="RevenueCat environment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="rounded-lg border border-border bg-surface-muted px-3 py-2.5 text-xs text-ink-muted">
                <a
                  href={REVENUECAT_KEYS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-accent underline-offset-2 hover:underline"
                >
                  Create a RevenueCat v2 secret key
                </a>{' '}
                with project read, Charts &amp; Metrics overview and charts
                read, and integration read/write permissions. Customer access is
                not required.
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              <Plug />
              Connect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const describeStatus = (integration: Integration): string => {
  switch (integration.status) {
    case 'connected':
      return 'Connected, waiting for first sync';
    case 'backfilling':
      if (integration.provider === 'revenuecat') {
        return 'Refreshing RevenueCat metrics';
      }
      if (integration.provider === 'google_search_console') {
        return 'Importing search history';
      }
      return 'Importing history';
    case 'live':
      return 'Ready';
    case 'error':
      return 'Needs attention';
    case 'disconnected':
      return 'Disconnected';
    default:
      return integration.status;
  }
};

const providerName = (provider: Integration['provider']): string => {
  switch (provider) {
    case 'revenuecat':
      return 'RevenueCat';
    case 'google_search_console':
      return 'Google Search Console';
    case 'stripe':
      return 'Stripe';
  }
};

const hasRevenueCatRun = (integration: Integration): boolean =>
  integration.provider === 'revenuecat' &&
  typeof integration.statusDetail?.runId === 'string';

const isHistoricalImportActive = (integration: Integration): boolean =>
  integration.status === 'backfilling' &&
  (integration.provider !== 'revenuecat' || hasRevenueCatRun(integration));

export const IntegrationsSection = ({
  project,
  canManage,
}: {
  project: Project;
  canManage: boolean;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [stripeConnectOpen, setStripeConnectOpen] = React.useState(false);
  const [revenueCatConnectOpen, setRevenueCatConnectOpen] =
    React.useState(false);
  const [searchConsoleConnectOpen, setSearchConsoleConnectOpen] =
    React.useState(false);
  const [pendingDisconnect, setPendingDisconnect] =
    React.useState<Integration | null>(null);
  const [pendingResync, setPendingResync] = React.useState<Integration | null>(
    null,
  );
  const [startingResync, setStartingResync] = React.useState<{
    integrationId: string;
    previousUpdatedAt: string;
  } | null>(null);

  const integrationsQuery = useQuery({
    queryKey: ['integrations', project.id],
    queryFn: () => integrationApi.listIntegrations(project.id),
    // Backfills update asynchronously; keep the status truthful while this
    // settings panel is open without requiring a manual refresh.
    refetchInterval: 5_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['integrations', project.id] });
  };

  const disconnectMutation = useMutation({
    mutationFn: (integrationId: string) =>
      integrationApi.disconnectIntegration(project.id, integrationId),
    onSuccess: () => {
      setPendingDisconnect(null);
      invalidate();
    },
    onError: (err) => {
      setPendingDisconnect(null);
      captureError(err);
    },
  });

  const resyncMutation = useMutation({
    mutationFn: (request: {
      integrationId: string;
      previousUpdatedAt: string;
    }) => integrationApi.resyncIntegration(project.id, request.integrationId),
    onMutate: (request) => setStartingResync(request),
    onSuccess: () => {
      setPendingResync(null);
      invalidate();
    },
    onError: (err) => {
      setStartingResync(null);
      captureError(err);
    },
  });

  const integrations = integrationsQuery.data?.integrations ?? [];

  React.useEffect(() => {
    if (!startingResync) {
      return;
    }
    const integration = integrations.find(
      (candidate) => candidate.id === startingResync.integrationId,
    );
    if (
      !integration ||
      isHistoricalImportActive(integration) ||
      (integration.updatedAt !== startingResync.previousUpdatedAt &&
        (integration.status === 'live' || integration.status === 'error'))
    ) {
      setStartingResync(null);
    }
  }, [integrations, startingResync]);

  return (
    <div className="px-5 py-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
          Integrations
        </h3>
        {canManage ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                clearError();
                setSearchConsoleConnectOpen(true);
              }}
            >
              <Search />
              Search Console
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                clearError();
                setRevenueCatConnectOpen(true);
              }}
            >
              <Plug />
              RevenueCat
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                clearError();
                setStripeConnectOpen(true);
              }}
            >
              <Plug />
              Stripe
            </Button>
          </div>
        ) : null}
      </div>

      {error ? (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {integrationsQuery.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : integrations.length === 0 ? (
        <p className="py-2 text-sm text-ink-muted">
          Nothing connected. Add search performance or revenue data to see the
          full path from discovery to purchase.
        </p>
      ) : (
        <ul className="space-y-2">
          {integrations.map((integration) => {
            const importing =
              startingResync?.integrationId === integration.id ||
              isHistoricalImportActive(integration);
            const retryingLegacyImport =
              integration.provider === 'revenuecat' &&
              integration.status === 'backfilling' &&
              !hasRevenueCatRun(integration);
            return (
              <li
                key={integration.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                    {providerName(integration.provider)}
                    {integration.displayName ? (
                      <span className="font-normal text-ink-muted">
                        {integration.displayName}
                      </span>
                    ) : null}
                    {integration.provider !== 'google_search_console' ? (
                      <Badge
                        tone={integration.livemode ? 'neutral' : 'warning'}
                      >
                        {integration.livemode ? 'Production' : 'Test mode'}
                      </Badge>
                    ) : null}
                    <Badge tone={STATUS_TONE[integration.status]}>
                      {describeStatus(integration)}
                    </Badge>
                  </p>
                  <p className="mt-0.5 truncate font-mono text-xs text-ink-muted">
                    {integration.externalAccountId}
                    {integration.defaultCurrency
                      ? ` · ${integration.defaultCurrency.toUpperCase()}`
                      : ''}
                  </p>
                </div>

                <span className="hidden text-xs text-ink-subtle sm:block">
                  connected {formatRelative(integration.createdAt)}
                </span>

                {canManage ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={importing}
                      aria-label={
                        integration.provider === 'revenuecat'
                          ? `${importing ? 'Refreshing' : 'Refresh'} RevenueCat metrics`
                          : integration.provider === 'google_search_console'
                            ? `${importing ? 'Importing' : 'Reimport'} Search Console history`
                            : `${importing ? 'Importing' : 'Reimport'} Stripe history`
                      }
                      title={
                        importing
                          ? integration.provider === 'revenuecat'
                            ? 'RevenueCat metrics are already refreshing.'
                            : 'A full historical import is already in progress.'
                          : integration.provider === 'revenuecat'
                            ? 'Fetches the latest aggregate metrics from RevenueCat. No customer records are imported.'
                            : integration.provider === 'google_search_console'
                              ? 'Fetches Search Console performance history again and updates existing rows.'
                              : 'Fetches all provider history again and updates existing records.'
                      }
                      onClick={() => {
                        clearError();
                        setPendingResync(integration);
                      }}
                    >
                      <RefreshCw />
                      {importing
                        ? integration.provider === 'revenuecat'
                          ? 'Refreshing…'
                          : 'Importing…'
                        : integration.provider === 'revenuecat'
                          ? 'Refresh metrics'
                          : retryingLegacyImport ||
                              integration.status === 'error'
                            ? 'Retry import'
                            : 'Reimport history'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Disconnect ${integration.provider}`}
                      onClick={() => {
                        clearError();
                        setPendingDisconnect(integration);
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <ConnectStripeDialog
        project={project}
        open={stripeConnectOpen}
        onOpenChange={setStripeConnectOpen}
      />
      <ConnectRevenueCatDialog
        project={project}
        open={revenueCatConnectOpen}
        onOpenChange={setRevenueCatConnectOpen}
      />
      <ConnectSearchConsoleDialog
        project={project}
        open={searchConsoleConnectOpen}
        onOpenChange={setSearchConsoleConnectOpen}
      />

      <ConfirmDialog
        open={Boolean(pendingResync)}
        onOpenChange={(open) => !open && setPendingResync(null)}
        title={
          pendingResync?.provider === 'revenuecat'
            ? 'Refresh RevenueCat metrics?'
            : pendingResync?.provider === 'google_search_console'
              ? 'Reimport Search Console history?'
              : 'Reimport Stripe history?'
        }
        description={
          pendingResync?.provider === 'revenuecat'
            ? 'GainSignal will fetch RevenueCat’s current overview and revenue totals again. It does not import subscribers and usually finishes in seconds.'
            : pendingResync?.provider === 'google_search_console'
              ? 'GainSignal will fetch the available Search Console history again. Existing rows are updated, not duplicated.'
              : 'GainSignal will fetch your complete Stripe history again. Existing records are updated, not duplicated.'
        }
        confirmLabel={
          pendingResync?.provider === 'revenuecat'
            ? 'Refresh metrics'
            : 'Reimport history'
        }
        confirmVariant="primary"
        loading={resyncMutation.isPending}
        onConfirm={() => {
          if (pendingResync) {
            resyncMutation.mutate({
              integrationId: pendingResync.id,
              previousUpdatedAt: pendingResync.updatedAt,
            });
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(pendingDisconnect)}
        onOpenChange={(open) => !open && setPendingDisconnect(null)}
        title={`Disconnect ${pendingDisconnect ? providerName(pendingDisconnect.provider) : 'integration'}?`}
        description="GainSignal stops receiving provider events and forgets the key. Revenue data already imported is kept."
        confirmLabel="Disconnect"
        loading={disconnectMutation.isPending}
        onConfirm={() => {
          if (pendingDisconnect) {
            disconnectMutation.mutate(pendingDisconnect.id);
          }
        }}
      />
    </div>
  );
};
